import { useEffect } from "react";

import { useCurrentActor } from "@/lib/current-actor";

/**
 * Story 18.2 — applies the signed-in person's own stored `theme_preference`
 * to `<html data-theme>`, which is all Story 18.1's own CSS cascade
 * (`:root[data-theme="dark"]`, `index.css`) needs to pick the dark token set.
 * `null` (never chosen — every existing user, until they toggle it) removes
 * the attribute entirely, leaving `index.css`'s own OS-color-scheme media
 * query fallback in charge — exactly today's behavior, unchanged.
 *
 * Mounted once, in `RequireAuth.tsx`'s authenticated branch, alongside
 * `IdleSessionGuard` (judgement call 3) — not per-component, and not
 * conditioned on any one screen.
 *
 * A brief flash of the wrong theme before `useCurrentActor()` resolves is an
 * accepted v1 tradeoff (judgement call 4, spec's own Out of scope): a
 * zero-flash mechanism needs a pre-React bootstrap script, real new
 * complexity this story does not ask for.
 *
 * Found live verifying this story: `RequireAuth.tsx` only renders this
 * component in its authenticated branch, so signing out unmounts it — the
 * unmount cleanup below removes the attribute rather than leaving it in
 * place, or a signed-out visitor's public `/sign-in` screen (and the next
 * person to sign in on a shared browser, before their own preference loads)
 * would silently inherit whatever theme the previous session left behind.
 */
export function ThemeSync() {
  const { data } = useCurrentActor();
  const preference = data?.theme_preference ?? null;

  useEffect(() => {
    if (preference) {
      document.documentElement.setAttribute("data-theme", preference);
    } else {
      document.documentElement.removeAttribute("data-theme");
    }
    return () => {
      document.documentElement.removeAttribute("data-theme");
    };
  }, [preference]);

  return null;
}
